import cx from 'classnames';
import type { KeyboardEvent, ReactElement, ReactNode } from 'react';
import { deprecate } from '../../utils/deprecate';
import { ESCAPE } from '../../utils/keyCodes';
import { Icon } from '../Icon';
import { Typography } from '../Typography';
import { DismissButton } from './DismissButton';
import styles from './styles.module.css';

export type MessageType = 'success' | 'warning' | 'danger';

const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>, cb: () => void): void => {
  if (event.keyCode === ESCAPE) {
    cb();
  }
};

const getIcon = (type?: MessageType): ReactElement => {
  switch (type) {
    case 'success':
      return <Icon name="check" />;
    case 'danger':
      return <Icon name="alert-triangle" />;
    case 'warning':
      return <Icon name="alert-circle" />;
    default:
      return <Icon name="info" />;
  }
};

export interface MessageProps {
  type?: MessageType;
  children: NonNullable<ReactNode>;
  noRadius?: boolean;
  centeredText?: boolean;
  onDismiss?: () => void;
}

/** @deprecated use Message from kitt-universal instead */
export function Message({ type, children, onDismiss, noRadius, centeredText, ...props }: MessageProps): ReactNode {
  if (__DEV__) deprecate(Message.name, 'Use Message from kitt-universal instead');
  const isWhite = false;

  return (
    <div
      className={cx(styles.Alert, {
        [styles.Alert_Success]: type === 'success',
        [styles.Alert_Warning]: type === 'warning',
        [styles.Alert_Danger]: type === 'danger',
        [styles.Dismissable]: !!onDismiss,
        [styles.WithRadius]: !noRadius,
        [styles.Center]: centeredText,
      })}
      {...props}
    >
      <Typography.div className={styles.Content} color={isWhite ? 'white' : 'black'} variant="bold">
        {!centeredText && getIcon(type)}
        <div className={styles.Text}>{children}</div>
      </Typography.div>
      {onDismiss && (
        <div className={styles.MediumDismiss}>
          <DismissButton type={type} onClick={onDismiss} />
        </div>
      )}
      {onDismiss && (
        <div
          role="button"
          tabIndex={0}
          className={styles.BaseDismiss}
          aria-label="Dismiss"
          onClick={onDismiss}
          onKeyDown={(e): void => handleKeyDown(e, onDismiss)}
        />
      )}
    </div>
  );
}
